import { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, CardActions, CardHeader, Divider, Grid, TextField, FormControlLabel, Checkbox, FormControl, InputLabel, Select, MenuItem, Chip, OutlinedInput,
} from '@mui/material';
import axios from '../../../../services/ApiService';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../../support/LoadingSpinner';
import { Helmet } from 'react-helmet-async';
import { ProgramData } from '../../../types/ProgramTypes';
import { BVTConstants } from '../../../../utils/BVTConstants';
import Swal from 'sweetalert2';
import { CompanyCategory } from '../../../types/DataTypes';

const ProgramForm = () => {
  const [programId, setProgramId] = useState(0);
  const [programName, setProgramName] = useState('');
  const [description, setDescription] = useState('');
  const [needsSubscription, setNeedsSubscription] = useState(false);
  const [amount, setAmount] = useState(0);
  const [frequency, setFrequency] = useState('MONTHLY');
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [categories, setCategories] = useState<CompanyCategory[]>([]);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Track original subscription values for comparison
  const [originalHasSubscription, setOriginalHasSubscription] = useState(false);
  const [originalAmount, setOriginalAmount] = useState(0);
  const [originalFrequency, setOriginalFrequency] = useState('MONTHLY');
  
  const navigate = useNavigate();
  const location = useLocation();
  const { id, name, desc } = location.state || {};

  useEffect(() => {
    if (id) {
      setProgramId(id);
      setProgramName(name);
      setDescription(desc);
      setIsEdit(true);
      fetchProgramData(id);
    }
    fetchCategories();
  }, [id]);

  const fetchProgramData = async (programId: number) => {
    try {
      const response = await axios.post('/getProgram', programId);
      const programData = response.data;
      
      // Set the current values
      setNeedsSubscription(programData.hasSubscription || false);
      setAmount(programData.amount || 0);
      setFrequency(programData.frequency || 'MONTHLY');
      setSelectedCategories(programData.categories?.map((cat: any) => cat.catId) || []);
      
      // Set the original values for comparison
      setOriginalHasSubscription(programData.hasSubscription || false);
      setOriginalAmount(programData.amount || 0);
      setOriginalFrequency(programData.frequency || 'MONTHLY');
    } catch (error) {
      console.error('Error fetching program data:', error);
      toast.error('Error loading program data');
    }
  };

  const fetchCategories = async () => {
    setCategories([]);
    setLoading(true);
    try {
      const response = await axios.get('/getCompanyCategories');
      if (Array.isArray(response.data)) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Error fetching categories.');
    } finally {
      setLoading(false);
    }
  };

  const hasSubscriptionChanged = () => {
    if (!isEdit) return false;
    
    return (
      needsSubscription !== originalHasSubscription ||
      (needsSubscription && (
        amount !== originalAmount || 
        frequency !== originalFrequency
      ))
    );
  };

  const handleCategoryChange = (event: any) => {
    const value = event.target.value;
    setSelectedCategories(typeof value === 'string' ? value.split(',').map(Number) : value);
  };

  const handleSave = async () => {
    // Validate program name and description
    if (!programName.trim()) {
        toast.error('Program name is required');
        return;
    }

    if (!description.trim()) {
        toast.error('Program description is required');
        return;
    }

    // Validate subscription fields if needed
    if (needsSubscription) {
        if (!amount || amount <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }
        if (!frequency) {
            toast.error('Please select a frequency');
            return;
        }
        if (!selectedCategories || selectedCategories.length === 0) {
            toast.error('Please select at least one category');
            return;
        }
    }

    // Check if subscription details have changed in edit mode
    if (isEdit && hasSubscriptionChanged()) {
      const result = await Swal.fire({
        title: 'Subscription Changes Detected',
        text: 'New subscription model will be applied to existing subscribers also. Do you want to continue?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Continue',
        cancelButtonText: 'Cancel'
      });

      if (!result.isConfirmed) {
        return;
      }
    }

    const programData: ProgramData = {
      id: programId,
      programName: programName,
      description: description,
      hasSubscription: needsSubscription,
      amount: needsSubscription ? amount : 0,
      currency: 'AUD',
      frequency: frequency,
      categories: selectedCategories.map(catId => categories.find(cat => cat.catId === catId)).filter(Boolean),
      status: BVTConstants.ACTIVE,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      workflows: [],
    };

    setLoading(true);
    try{
      if(isEdit){
        await axios.post('/updateProgram', programData);
        console.log('Program updated successfully');
        toast.success("Program Updated Successfully.");
        navigate(-1);
      }else{
        const response = await axios.post('/createProgram', programData);
        console.log('Program saved successfully');
        toast.success("Program Saved Successfully.");
        
        // Navigate to workflow creation page with the new program ID
        if (response.data && response.data.id) {
          navigate('/admin/workflows/new', { 
            state: { 
              programId: response.data.id,
              programName: programName 
            } 
          });
        } else {
          navigate(-1);
        }
      }
    } catch (error) {
      console.error('Error saving program.');
      toast.error('Error saving program. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Helmet><title>BVT Admin - Programs</title></Helmet>
    <Card>
      <CardHeader title={`${isEdit ? 'Update' : 'Create'} Program`} sx={{ paddingBottom: 1 }} />
      <Divider />
      <CardContent sx={{ paddingTop: 2 }}>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              required
              label="Program Name"
              value={programName}
              onChange={(e) => setProgramName(e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              required
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>
        </Grid>

        <Box mt={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={needsSubscription}
                onChange={(e) => setNeedsSubscription(e.target.checked)}
                color="primary"
              />
            }
            label="Needs Subscription"
          />
        </Box>

        {needsSubscription && (
          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  required
                  label="Amount"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  inputProps={{
                    step: "0.01",
                    min: "0",
                    lang: "en-US"
                  }}
                  helperText="Enter the subscription amount"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth required>
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                    label="Frequency"
                  >
                    <MenuItem value="MONTHLY">Monthly</MenuItem>
                    <MenuItem value="QUARTERLY">Quarterly</MenuItem>
                    <MenuItem value="YEARLY">Yearly</MenuItem> 
                    <MenuItem value="ONE_TIME">One Time</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Categories</InputLabel>
                  <Select
                    multiple
                    value={selectedCategories}
                    onChange={handleCategoryChange}
                    input={<OutlinedInput label="Categories" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const category = categories.find(cat => cat.catId === value);
                          return (
                            <Chip 
                              key={value} 
                              label={category?.catName || value} 
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.catId} value={category.catId}>
                        {category.catName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        )}

      </CardContent>
      <Divider />
      <CardActions sx={{ justifyContent: 'flex-end' }}>
        <Button variant="contained" color='error' onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          {isEdit ? 'Update' : 'Save'} Program
        </Button>
      </CardActions>
    </Card>
    <LoadingSpinner loading={loading} />
    </>
  );
};

export default ProgramForm;
